import { FC } from "react"
import { Box, Typography } from "@strapi/design-system"
import { GlassesProduct } from "types"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "hooks/storeHooks"
import { setGlassesRenderingOptionsByKey } from "store/product/productSlice"
import { GlassesEffect } from "services/player"
import SliderSelectWithInput from "@/components/SliderSelectWithInput/SliderSelectWithInput"

interface GlassesRenderingOptionsProps {
  glassesItem: GlassesProduct
}

export const GlassesRenderingOptions: FC<GlassesRenderingOptionsProps> = ({ glassesItem }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const handleScaleChange = (axis: string, value: number) => {
    if (value > 2.5) value = 2.5
    if (value < -2.5) value = -2.5
    const newScale = {
      ...(glassesItem.renderingOptions.scale || { x: 0, y: 0, z: 0 }),
      [axis]: value.toFixed(2),
    }
    dispatch(setGlassesRenderingOptionsByKey({ key: "scale", value: newScale }))
    GlassesEffect.setGlassesScale(newScale.x, newScale.y, newScale.z)
  }

  const handlePositionChange = (axis: string, value: number) => {
    if (value > 100) value = 100
    if (value < -100) value = -100
    const newPosition = {
      ...(glassesItem.renderingOptions.positionAccuracy || { x: 0, y: 0, z: 0 }),
      [axis]: value.toFixed(1),
    }
    dispatch(setGlassesRenderingOptionsByKey({ key: "positionAccuracy", value: newPosition }))
    GlassesEffect.setGlassesPosition(newPosition.x, newPosition.y, newPosition.z)
  }

  return (
    <Box
      padding={8}
      hasRadius
      shadow="tableShadow"
      background="neutral0"
      style={{
        gridRowStart: 2,
        gridColumnStart: 1,
        gridRowEnd: 2,
        gridColumnEnd: 1,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Box style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}>
        <Box>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "start",
              padding: "0",
              gap: 5,
              minWidth: "400px",
            }}
          >
            <Typography
              variant="omega"
              style={{ fontWeight: "700", fontSize: "19px", fontHeight: "19px" }}
            >
              {t("glassesImagesPage.scale")}
            </Typography>
            <Box
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 12,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div className="flex flex-col">
                <SliderSelectWithInput
                  title="X"
                  itemKey="x"
                  value={Number(glassesItem?.renderingOptions?.scale?.x ?? 0)}
                  inputOnChange={handleScaleChange}
                  min={-2.5}
                  max={2.5}
                  step={0.01}
                />
                <SliderSelectWithInput
                  title="Y"
                  itemKey="y"
                  value={Number(glassesItem?.renderingOptions?.scale?.y ?? 0)}
                  inputOnChange={handleScaleChange}
                  min={-2.5}
                  max={2.5}
                  step={0.01}
                />
                <SliderSelectWithInput
                  title="Z"
                  itemKey="z"
                  value={Number(glassesItem?.renderingOptions?.scale?.z ?? 0)}
                  inputOnChange={handleScaleChange}
                  min={-2.5}
                  max={2.5}
                  step={0.01}
                />
              </div>
            </Box>
          </Box>
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "start",
            padding: "0",
            gap: 5,
            minWidth: "400px",
          }}
        >
          <Typography
            variant="omega"
            style={{ fontWeight: "700", fontSize: "19px", fontHeight: "19px" }}
          >
            {t("glassesImagesPage.positionAccuracy")}
          </Typography>
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 12,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="flex flex-col">
              <SliderSelectWithInput
                title="X"
                itemKey="x"
                value={Number(glassesItem?.renderingOptions?.positionAccuracy?.x ?? 0)}
                inputOnChange={handlePositionChange}
                min={-100}
                max={100}
                step={0.1}
              />
              <SliderSelectWithInput
                title="Y"
                itemKey="y"
                value={Number(glassesItem?.renderingOptions?.positionAccuracy?.y ?? 0)}
                inputOnChange={handlePositionChange}
                offset={2}
                min={-100}
                max={100}
                step={0.1}
              />
              <SliderSelectWithInput
                title="Z"
                itemKey="z"
                value={Number(glassesItem?.renderingOptions?.positionAccuracy?.z ?? 0)}
                inputOnChange={handlePositionChange}
                offset={5}
                min={-100}
                max={100}
                step={0.1}
              />
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
